import { type ReactNode, useMemo } from 'react'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import { CleanThemeProvider } from 'shared/theme/cleanThemeProvider'
import { useLoginStyle } from 'shared/hooks/useLoginStyle'
import { useTenantBranding } from 'shared/hooks/useTenantBranding'
import { useTranslation } from 'shared/i18n'
import { RouterLink } from 'shared/routes/components'

// Figma: 認証画面共通レイアウト
// - 背景 #ededed / 中央にカード(w560, #fcfcfc, 角丸8px) / 下部にフッター
// - フッター: ヘルプセンター・利用規約・プライバシーポリシー・運営会社 + コピーライト
// - テナント判定とヘルプ導線は useTenantBranding、sooki の背景は useLoginStyle から取る

interface AuthLayoutProps {
  children: ReactNode
}

const FOOTER_LINK_SX = {
  fontFamily: '"Noto Sans JP", sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: 1,
  color: 'rgba(0, 0, 0, 0.6)',
  textDecoration: 'none',
  whiteSpace: 'nowrap'
} as const

function AuthLayoutContent ({ children }: AuthLayoutProps): JSX.Element {
  const { t } = useTranslation()
  const { backgroundStyle } = useLoginStyle()
  // ヘルプ導線(sooki=取扱説明書)は SSO フッターと同じく useTenantBranding から取る。
  const { tenantName, helpCenterUrl, helpCenterLabel } = useTenantBranding()
  const companyLink = useMemo(
    () => (tenantName === 'sooki' ? 'https://sooki.co.jp/' : 'https://jizaie.co.jp/'),
    [tenantName]
  )
  // コピーライト年は描画時の年に追従(年跨ぎでハードコードが古くならないように)
  const copyrightYear = new Date().getFullYear()

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#ededed',
        ...backgroundStyle
      }}
    >
      {/* 本体: 中央寄せのカード */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 5
        }}
      >
        <Box
          sx={{
            width: 560,
            maxWidth: '100%',
            bgcolor: '#fcfcfc',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            pt: 5,
            pb: 2.5,
            px: 5
          }}
        >
          {children}
        </Box>
      </Box>

      {/* フッター */}
      {/* Figma(desktop): ナビをビューポート中央、コピーライトを右端に置く。
          md+ は grid 3カラム(左余白 / 中央ナビ / 右コピーライト)で中央寄せを保ち、
          狭幅(xs)では縦積みにして絶対配置由来の重なりを防ぐ。 */}
      <Box
        component="footer"
        sx={{
          minHeight: 48,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
          alignItems: 'center',
          justifyItems: 'center',
          rowGap: 1,
          bgcolor: '#fcfcfc',
          borderTop: '1px solid #ededed',
          px: 2.5,
          py: { xs: 1.5, md: 0 }
        }}
      >
        {/* 左余白(md+のみ): 中央ナビを真ん中に保つ */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 3, alignItems: 'center' }}>
          <Link href={helpCenterUrl} rel="noopener noreferrer" sx={FOOTER_LINK_SX} target="_blank">
            {helpCenterLabel}
          </Link>
          <Link component={RouterLink} href="/terms-of-use" sx={FOOTER_LINK_SX}>
            {t('auth.authLayout.link')}
          </Link>
          <Link component={RouterLink} href="/privacy-policy" sx={FOOTER_LINK_SX}>
            {t('auth.authLayout.link2')}
          </Link>
          <Link href={companyLink} rel="noopener noreferrer" sx={FOOTER_LINK_SX} target="_blank">
            {t('auth.authLayout.link3')}
          </Link>
        </Box>
        <Box
          sx={{
            justifySelf: { xs: 'center', md: 'end' },
            fontFamily: '"Noto Sans JP", sans-serif',
            fontSize: '11px',
            fontWeight: 500,
            color: 'rgba(0, 0, 0, 0.6)',
            whiteSpace: 'nowrap'
          }}
        >
          © {copyrightYear}. JIZAIE,Inc. All rights reserved
        </Box>
      </Box>
    </Box>
  )
}

export default function AuthLayout (props: AuthLayoutProps): JSX.Element {
  return (
    <CleanThemeProvider>
      <AuthLayoutContent {...props} />
    </CleanThemeProvider>
  )
}
